import type { RootState } from 'app/store';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';

import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useMatches } from 'react-router-dom';

type RouteHandle = {
    analyticsName?: string;
};

export const SpentTimeRecorder = ({ children }: { children?: React.ReactNode }): JSX.Element => {
    const location = useLocation();
    const matches = useMatches();
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const analyticsMatch = [...matches].reverse().find((m) => (m.handle as RouteHandle | undefined)?.analyticsName);

    const name = (analyticsMatch?.handle as RouteHandle | undefined)?.analyticsName;

    const projectId = matches.map((m) => m.params.id).find(Boolean);

    const projectData = useSelector((state: RootState) =>
        projectId ? state.requests?.[`getProject_${projectId}`]?.data?.project?.data : undefined,
    );

    const pageState = useRef({
        startTime: performance.now(),
        name,
        projectData,
        pathname: location.pathname,
    });

    const sendAnalytics = useCallback(
        (pageData: typeof pageState.current) => {
            if (!pageData.name) {
                return;
            }

            const duration = Math.round((performance.now() - pageData.startTime) / 1000);

            if (duration < 1) {
                return;
            }

            sendGpnAnalyticsEvent({
                componentId: 'rim_project page_visited',
                component: 'PAGE',
                projectId: pageData.projectData?.uniqueId,
                calculationId: pageData.projectData?.calculationId,
                projectStage: pageData.projectData?.stage,
                methodology: pageData.projectData?.methodology,
                extraParams: [
                    { name: 'time_spent_seconds', type: 'INT', value: duration },
                    { name: 'page_name', type: 'STRING', value: pageData.name },
                    { name: 'page_url', type: 'STRING', value: window.location.origin + pageData.pathname },
                ],
            });
        },
        [sendGpnAnalyticsEvent],
    );

    useEffect(() => {
        sendAnalytics(pageState.current);

        pageState.current = {
            startTime: performance.now(),
            name,
            projectData,
            pathname: location.pathname,
        };
    }, [location.pathname, sendAnalytics, name, projectData]);

    useEffect(() => {
        const handleUnload = () => sendAnalytics(pageState.current);

        window.addEventListener('beforeunload', handleUnload);

        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [location.pathname, sendAnalytics]);

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
};

SpentTimeRecorder.displayName = 'SpentTimeRecorder';
